// This guide mirrors the server-owned Platform 02 catalogs:
// - flex-interativa-server/server/platform2-score-defaults.json
// - flex-interativa-plataforma-eventos-server/server/achievement-catalog.js
// The score values below are defaults for newly provisioned events. Administrators
// can change event-local score values in Networking > Ranking.

export const PLATFORM02_SCORE_RULES = Object.freeze([
  {
    id: 1,
    type: "networking_connection",
    title: "Conexão de networking aceita",
    defaultValue: 10,
    logic: "Uma solicitação de conexão comum é aceita entre dois participantes.",
  },
  {
    id: 2,
    type: "networking_qr_connection",
    title: "Conexão por QR Code",
    defaultValue: 20,
    logic: "Uma conexão entre participantes é concluída pelo fluxo de QR Code.",
  },
  {
    id: 3,
    type: "interest_point_collection",
    title: "Ponto de interesse coletado",
    defaultValue: 25,
    logic: "O participante coleta um ponto de interesse ativo pelo QR Code. Cada POI usa a regra salva para o evento.",
  },
  {
    id: 4,
    type: "exhibitor_visit",
    title: "Expositor visitado por QR Code",
    defaultValue: 25,
    logic: "O participante coleta o QR Code de um expositor visível e coletável.",
  },
  {
    id: 5,
    type: "activity_photo_approval",
    title: "Envio de foto",
    defaultValue: 100,
    logic: "Uma foto enviada em uma atividade de foto é aprovada pela moderação.",
  },
  {
    id: 6,
    type: "activity_photo_competition_winner",
    title: "Competição de fotos",
    defaultValue: 1000,
    logic: "A competição é finalizada e o participante fica em primeiro lugar, inclusive em empate válido.",
  },
  {
    id: 7,
    type: "schedule_interest",
    title: "Interesse ou confirmação em agenda",
    defaultValue: 5,
    logic: "O participante marca interesse ou confirma presença em um item da agenda. Cada item diferente pode gerar a pontuação.",
  },
  {
    id: 8,
    type: "participant_checkin_day",
    title: "Check-in em um dia do evento",
    defaultValue: 50,
    logic: "Um check-in é confirmado. Cada data de check-in gera uma ocorrência distinta.",
  },
  {
    id: 9,
    type: "activity_quiz_completed",
    title: "Quiz",
    defaultValue: null,
    logic: "A pontuação no ranking é a soma verificada dos “Pontos por acerto” definidos em cada pergunta.",
  },
  {
    id: 10,
    type: "networking_connections_5",
    title: "Marco de 5 conexões",
    defaultValue: 25,
    logic: "O participante alcança cinco conexões de networking aceitas no evento.",
  },
  {
    id: 11,
    type: "networking_connections_10",
    title: "Marco de 10 conexões",
    defaultValue: 50,
    logic: "O participante alcança dez conexões de networking aceitas no evento.",
  },
  {
    id: 12,
    type: "networking_connections_25",
    title: "Marco de 25 conexões",
    defaultValue: 100,
    logic: "O participante alcança 25 conexões de networking aceitas no evento.",
  },
  {
    id: 13,
    type: "profile_complete",
    title: "Perfil completo",
    defaultValue: 50,
    logic: "Todos os campos obrigatórios visíveis são preenchidos e, quando exigida pelo evento, a foto de perfil é adicionada.",
  },
  {
    id: 15,
    type: "stream_watch_15m",
    title: "15 minutos de transmissões assistidos",
    defaultValue: 25,
    logic: "Alcança 15 minutos acumulados de transmissões em primeiro plano. Os marcos de 15, 30 e 60 minutos são cumulativos.",
  },
  {
    id: 16,
    type: "stream_watch_30m",
    title: "30 minutos de transmissões assistidos",
    defaultValue: 50,
    logic: "Alcança 30 minutos acumulados de transmissões em primeiro plano e mantém o marco anterior.",
  },
  {
    id: 17,
    type: "stream_watch_60m",
    title: "60 minutos de transmissões assistidos",
    defaultValue: 100,
    logic: "Alcança 60 minutos acumulados de transmissões em primeiro plano e mantém os marcos anteriores.",
  },
  {
    id: 18,
    type: "stream_poll_answered",
    title: "Participação em enquete ao vivo",
    defaultValue: 25,
    logic: "O participante envia pelo menos um voto aceito em uma enquete de transmissão; esta regra concede um único marco no evento.",
  },
  {
    id: 19,
    type: "newsfeed_post_published",
    title: "Publicação manual no Newsfeed",
    defaultValue: 25,
    logic: "O participante mantém pelo menos uma publicação manual ativa no Newsfeed; esta regra concede um único marco no evento.",
  },
  {
    id: 20,
    type: "stream_full_engagement",
    title: "Engajamento completo em transmissão",
    defaultValue: 75,
    logic: "Na mesma transmissão, o participante reage, responde uma enquete e envia uma mensagem no chat.",
  },
  {
    id: 21,
    type: "activity_participant_voting_winner",
    title: "Votação",
    defaultValue: 1000,
    logic: "A atividade é finalizada e cada participante empatado na maior quantidade de votos válidos recebe a pontuação.",
  },
]);

const SCORE_RULE_GROUP_DEFINITIONS = [
  {
    id: "networking",
    title: "Networking",
    description: "Conexões entre participantes e marcos de relacionamento.",
    types: [
      "networking_connection",
      "networking_qr_connection",
      "networking_connections_5",
      "networking_connections_10",
      "networking_connections_25",
    ],
  },
  {
    id: "event_engagement",
    title: "Engajamento no evento",
    description: "Ações de presença, agenda, pontos de interesse e visitas a expositores.",
    types: ["interest_point_collection", "exhibitor_visit", "schedule_interest", "participant_checkin_day"],
  },
  {
    id: "profile",
    title: "Perfil",
    description: "Conclusão do perfil participante.",
    types: ["profile_complete"],
  },
  {
    id: "streaming",
    title: "Transmissões",
    description: "Tempo assistido e participação verificável em transmissões.",
    types: [
      "stream_watch_15m",
      "stream_watch_30m",
      "stream_watch_60m",
      "stream_poll_answered",
      "stream_full_engagement",
    ],
  },
  {
    id: "newsfeed",
    title: "Newsfeed",
    description: "Publicações manuais no Newsfeed.",
    types: ["newsfeed_post_published"],
  },
  {
    id: "activities",
    title: "Atividades",
    description: "Quiz verificado, envio de foto, competição e votação.",
    types: [
      "activity_quiz_completed",
      "activity_photo_approval",
      "activity_photo_competition_winner",
      "activity_participant_voting_winner",
    ],
  },
];

export const PLATFORM02_SCORE_GROUPS = Object.freeze(SCORE_RULE_GROUP_DEFINITIONS.map((group) => ({
  ...group,
  items: PLATFORM02_SCORE_RULES.filter((rule) => group.types.includes(rule.type)),
})));

const achievement = (code, title, target, logic) => ({ code, title, target, logic });

export const PLATFORM02_ACHIEVEMENT_GROUPS = Object.freeze([
  {
    id: "onboarding",
    title: "Início e retenção",
    items: [
      achievement("active_2_days", "Bom te ver de novo", "2 datas", "Participa do evento em duas datas diferentes."),
      achievement("active_3_days", "Presença constante", "3 datas", "Participa do evento em três datas diferentes."),
      achievement("active_5_days", "Veterano do evento", "5 datas", "Participa do evento em cinco datas diferentes."),
      achievement("all_event_pages_viewed", "Conhece cada canto", "Todas as páginas", "Abre todas as seções informativas acessíveis e habilitadas; a meta é dinâmica."),
    ],
  },
  {
    id: "agenda",
    title: "Agenda e presença",
    items: [
      achievement("vacancy_reserved_1", "Lugar garantido", "1 reserva", "Confirma a primeira reserva em um item da agenda."),
      achievement("vacancies_reserved_3", "Agenda disputada", "3 reservas", "Confirma reserva em três itens diferentes da agenda."),
    ],
  },
  {
    id: "networking",
    title: "Networking, QR e expositores",
    items: [
      achievement("friend_connection_1", "Nova conexão", "1 conexão", "Tem a primeira amizade aceita no evento."),
      achievement("friend_connections_5", "Networking em movimento", "5 conexões", "Tem cinco amizades aceitas."),
      achievement("friend_connections_10", "Rede em expansão", "10 conexões", "Tem dez amizades aceitas."),
      achievement("friend_connections_25", "Superconector", "25 conexões", "Tem 25 amizades aceitas."),
      achievement("interest_points_3", "Rota secreta", "3 POIs", "Coleta três pontos de interesse diferentes."),
      achievement("all_interest_points", "Mapa completo", "Todos os POIs", "Coleta todos os pontos de interesse ativos e coletáveis; a meta é dinâmica."),
      achievement("exhibitor_qr_3", "Maratona de estandes", "3 expositores", "Coleta por QR Code três expositores diferentes."),
      achievement("exhibitor_qr_5", "Roteiro de negócios", "5 expositores", "Coleta por QR Code cinco expositores diferentes."),
      achievement("all_exhibitors_qr", "Feira completa", "Todos os expositores", "Coleta por QR Code todos os expositores visíveis e coletáveis; a meta é dinâmica."),
      achievement("networking_360", "Networking 360°", "3 condições", "Combina cinco conexões aceitas, três POIs coletados e três expositores coletados."),
    ],
  },
  {
    id: "social",
    title: "Conversas e Social",
    items: [
      achievement("dm_contacts_3", "Novas conversas", "3 contatos", "Troca mensagens com três contatos diretos diferentes."),
      achievement("dm_contacts_5", "Círculo ampliado", "5 contatos", "Troca mensagens com cinco contatos diretos diferentes."),
      achievement("newsfeed_post_1", "Compartilhou o momento", "1 publicação", "Cria a primeira publicação manual no Newsfeed."),
      achievement("newsfeed_posts_3", "Criador ativo", "3 publicações", "Cria três publicações manuais no Newsfeed."),
      achievement("social_likes_given_5", "Espalhou carinho", "5 curtidas", "Curte cinco conteúdos sociais diferentes."),
      achievement("social_likes_received_5", "Conteúdo querido", "5 curtidas recebidas", "Os conteúdos do participante recebem cinco curtidas."),
      achievement("social_likes_received_20", "Sucesso no feed", "20 curtidas recebidas", "Os conteúdos do participante recebem 20 curtidas."),
    ],
  },
  {
    id: "stream",
    title: "Transmissões",
    items: [
      achievement("stream_watch_15m", "Mergulho no conteúdo", "15 min", "Assiste 15 minutos acumulados em primeiro plano, limitados pela sessão validada."),
      achievement("stream_watch_30m", "Sessão completa", "30 min", "Assiste 30 minutos acumulados em primeiro plano, limitados pela sessão validada."),
      achievement("stream_watch_60m", "Maratonista do ao vivo", "60 min", "Assiste 60 minutos acumulados em primeiro plano, limitados pela sessão validada."),
      achievement("streams_joined_3", "Zapeando com propósito", "3 transmissões", "Participa de três transmissões diferentes."),
      achievement("stream_poll_votes_3", "Votante frequente", "3 enquetes", "Tem votos aceitos em três enquetes de transmissão diferentes."),
      achievement("stream_present_at_close", "Até o último minuto", "1 transmissão", "Permanece ao menos dez minutos e está presente no minuto final de uma transmissão encerrada."),
    ],
  },
]);

export const PLATFORM02_ACHIEVEMENT_COUNT = PLATFORM02_ACHIEVEMENT_GROUPS
  .reduce((total, group) => total + group.items.length, 0);
